/**
 * QuizEditorPage — Drop 1 (May 2026). Page shell only (decomposed in Phase 3).
 *
 * Trainer-facing per-question editor. Reachable from a quiz section's "Edit
 * questions" CTA in the Module Editor. All the backend endpoints used here
 * already existed before Drop 1; this page is the missing surface.
 *
 * Capabilities:
 *   - Inline edit text / options / correct flag / explanation / points
 *   - Drag-handle reorder via @dnd-kit
 *   - Add a question manually (MCQ or True/False)
 *   - Generate N more with AI on top of the current questions
 *   - Per-question Regenerate AI / Self-Audit / Approve / Reject / Delete
 *
 * Data + mutations live in useQuizEditor; question UI lives in
 * components/quiz/editor/*.
 */
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ArrowLeft, Plus, Sparkles, Loader2, Download, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import api from '../api/client';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { useQuizEditor } from '../hooks/useQuizEditor';
import { QuizEmptyState } from '../components/quiz/editor/QuizEmptyState';
import { ReviewSummaryBar } from '../components/quiz/editor/ReviewSummaryBar';
import { SortableQuestion } from '../components/quiz/editor/QuestionCard';
import { AddQuestionDialog } from '../components/quiz/editor/AddQuestionDialog';
import { QuizCsvImportDialog } from '../components/quiz/editor/QuizCsvImportDialog';

export default function QuizEditorPage() {
  const { moduleId, quizId } = useParams();
  const navigate = useNavigate();
  const {
    quiz,
    loading,
    refresh,
    genCount,
    setGenCount,
    generating,
    handleGenerateMore,
    bulkBusy,
    handleApproveAllPending,
    handleDragEnd,
    addOpen,
    setAddOpen,
  } = useQuizEditor(quizId);
  const [importOpen, setImportOpen] = useState(false);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/quizzes/${quizId}/export.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(quiz?.title || 'quiz').replace(/[^\w\- ]/g, '')}-questions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Quiz exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (loading) return <div className="p-6"><SkeletonCard /></div>;
  if (!quiz) return <div className="p-6">Quiz not found.</div>;

  const questions = quiz.questions || [];

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-4xl mx-auto relative" data-testid="quiz-editor-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-quiz)]" />
      <div className="relative z-10 space-y-5">
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/editor/modules/${moduleId}`)} data-testid="back-to-module">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to module
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="quiz-export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="quiz-import-csv-btn">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            variant="outline"
            data-testid="add-question-btn"
            className="text-[var(--lumyn-quiz)] border-[var(--lumyn-quiz)]/40 hover:bg-[var(--lumyn-quiz)]/10 hover:text-[var(--lumyn-quiz)]"
          >
            <Plus className="w-4 h-4 mr-1" /> Add question
          </Button>
        </div>
      </header>

      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-quiz)]/10 text-[var(--lumyn-quiz)] text-[11px] font-semibold uppercase tracking-[0.14em]">
          <Sparkles className="w-3.5 h-3.5" />Quiz editor
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="quiz-title">
          {quiz.title || 'Quiz'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {questions.length} question{questions.length === 1 ? '' : 's'}
          {quiz.passing_score != null ? ` · pass mark ${quiz.passing_score}%` : ''}
        </p>
      </div>

      {/* List */}
      {questions.length === 0 ? (
        <QuizEmptyState onAdd={() => setAddOpen(true)} onGen={handleGenerateMore} generating={generating} />
      ) : (
        <>
          <ReviewSummaryBar
            questions={questions}
            onApproveAll={handleApproveAllPending}
            busy={bulkBusy}
          />
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={questions.map(q => q.question_id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <SortableQuestion
                  key={q.question_id}
                  q={q}
                  idx={idx + 1}
                  quizId={quizId}
                  onChange={refresh}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        </>
      )}

      {/* Bottom toolbar */}
      {questions.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-4 border-t" data-testid="editor-bottom-toolbar">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Generate more (AI):</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={genCount}
              onChange={(e) => setGenCount(Math.max(1, Math.min(10, parseInt(e.target.value || '1', 10))))}
              className="w-16 h-9"
              data-testid="gen-more-count"
            />
            <Button
              onClick={handleGenerateMore}
              disabled={generating}
              data-testid="gen-more-btn"
              className="bg-[var(--lumyn-quiz)] hover:bg-[var(--lumyn-quiz)]/90 text-white shadow-md shadow-[var(--lumyn-quiz)]/20"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Generate
            </Button>
          </div>
          <Button onClick={() => setAddOpen(true)} variant="outline" data-testid="add-question-btn-bottom">
            <Plus className="w-4 h-4 mr-1" /> Add manually
          </Button>
        </div>
      )}

      <AddQuestionDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        quizId={quizId}
        onCreated={() => { setAddOpen(false); refresh(); }}
      />
      <QuizCsvImportDialog
        quizId={quizId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { setImportOpen(false); refresh(); }}
      />
      </div>
    </div>
  );
}
